import { useAppSelector } from "@/stores/hook";
import BasicDetailsEditMode from "./BasicDetailsEditMode";
import BasicDetailsViewMode from "./BasicDetailsViewMode";
import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useViewLeadFieldVisibility } from "@/features/leads/hook/useViewLeadFieldVisibility";
import { useLeadBasicDetailsExpanded } from "@/features/leads/api/useLeadFormConfig";

export default function BasicDetails() {
  const { isEditMode } = useAppSelector((state) => state.leadFunctional);
  const { watch } = useLeadForm();
  const { isFieldVisible } = useViewLeadFieldVisibility();
  const { lead } = useActiveDataMutation();

  // Settings → Manage → View Lead Display decides whether this card opens
  // expanded. TENANT-WIDE (server flag), not per-browser: the tenant sets it
  // once and every user opening any lead gets the same layout. Off by default,
  // so the drawer opens compact.
  const startExpanded = useLeadBasicDetailsExpanded();
  const [isExpanded, setIsExpanded] = useState(startExpanded);

  // Re-applied per lead, not just on mount: the drawer stays mounted while the
  // user moves from one lead to the next, so without the `lead?.id` dep the
  // first lead's expand state would stick for the rest of the session.
  // Edit mode always wins — mandatory fields must stay reachable.
  useEffect(() => {
    setIsExpanded(isEditMode || startExpanded);
  }, [isEditMode, startExpanded, lead?.id]);

  const { phone } = watch();

  // Display-only hide (Settings → Manage → View Lead Display). Edit mode is
  // exempt so mandatory fields can always be filled.
  if (!isEditMode && !isFieldVisible("basicDetails")) return null;

  return (
    <div className="border border-[#e6e8e7] rounded-[12px] overflow-hidden bg-white">
      <div
        className="px-3 py-2 cursor-pointer select-none hover:bg-[#f8faf9] transition-colors"
        onClick={() => setIsExpanded((p) => !p)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[14px] font-medium text-black tracking-[-0.28px] leading-normal">
              Basic details
            </span>
            {!isExpanded && (
              <span className="text-[12px] text-[#505e59] tracking-[-0.24px] truncate">
                — {lead?.name}
                {phone ? ` · ${phone}` : ""}
              </span>
            )}
          </div>

          {isExpanded ? (
            <ChevronUp className="size-3.5 text-[#697571] shrink-0" />
          ) : (
            <span className="text-[11px] text-[#9ba3a0] border border-dashed border-[#cdd1d0] rounded-[5px] px-1.5 py-0.5 shrink-0 hover:text-[#697571] hover:border-[#9ba3a0] transition-colors">
              expand
            </span>
          )}
        </div>
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          {isEditMode ? (
            <BasicDetailsEditMode embedded />
          ) : (
            <BasicDetailsViewMode embedded />
          )}
        </div>
      </div>
    </div>
  );
}
